import { Upload, message } from "antd";
import { UploadChangeParam, UploadFile } from "antd/lib/upload/interface";

import { InboxOutlined } from "@ant-design/icons";
import React from "react";
import { UploadRequestOption } from "rc-upload/lib/interface";
import axios from "@utils/axios";
import { selectUser } from "@store/slices/user";
import { useParams } from "react-router-dom";
import { useSaveUploadedGalleryMediaMutation } from "@store/surveys/surveys";
import { useSelector } from "react-redux";

const { Dragger } = Upload;

export interface UploadMediaFile<T = any> extends UploadFile<T> {}

export interface UploadMediaProps {
    onSuccessUpload?: (files: UploadMediaFile[]) => void;
    mimeTypes?: string[];
}

const UploadMedia: React.FC<UploadMediaProps> = ({ onSuccessUpload, mimeTypes }) => {
    const { token } = useSelector(selectUser);
    const { id: surveyId } = useParams();

    const [saveGalleryMedia] = useSaveUploadedGalleryMediaMutation();

    const handleChange = async (info: UploadChangeParam) => {
        const { status } = info.file;
        let done: boolean = true;

        if (onSuccessUpload) {
            info.fileList.forEach((file: UploadMediaFile) => {
                if (file.status !== "done") {
                    return (done = false);
                }
            });

            if (done) {
                onSuccessUpload(info.fileList);
            }
        }

        if (status === "done") {
            message.success(`${info.file.name} file uploaded successfully.`);
        } else if (status === "error") {
            message.error(`${info.file.name} file upload failed.`);
        }
    };

    const handleCustomRequest = async ({
        file,
        onProgress,
        onError,
        onSuccess
    }: UploadRequestOption) => {
        const _file = file as File;
        try {
            const response: any = await axios.post(
                "admin/media/signed-storage-url",
                {
                    content_type: _file.type,
                    expires: "",
                    visibility: "public-read"
                },
                {
                    headers: {
                        "X-Requested-With": "XMLHttpRequest",
                        "Content-type": "application/json",
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const { headers, bucket, key, uuid } = response.data;

            if ("Host" in headers) {
                delete headers.Host;
            }

            await axios.put(response.data.url, _file, {
                headers,
                onUploadProgress: (progressEvent: any) => {
                    if (onProgress) {
                        onProgress({
                            percent: (progressEvent.loaded / progressEvent.total) * 100
                        });
                    }
                }
            });

            const result = await saveGalleryMedia({
                surveyId,
                bucket,
                key,
                uuid,
                name: _file.name,
                mime: _file.type,
                size: _file.size
            });

            if (onSuccess && "data" in result) {
                onSuccess(result.data);
            } else if (onError && "error" in result) {
                message.error("File upload failed.");
            }
        } catch (error: any) {
            if (onError) {
                onError(error);
            }
        }
    };

    return (
        <div>
            <Dragger
                showUploadList={{ showRemoveIcon: false }}
                onChange={handleChange}
                multiple
                customRequest={handleCustomRequest}
                accept={"image/jpeg, image/png, image/webp, image/svg+xml"}
            >
                <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                </p>
                <p className="ant-upload-text">Click or drag file to this area to upload</p>
                <p className="ant-upload-hint">Support for a single or bulk upload</p>
            </Dragger>
        </div>
    );
};

export default UploadMedia;
